import { type UseMutationOptions, useMutation } from '@tanstack/react-query';

import {
  type CreateOrderInput,
  type CreateOrderResponse,
  ordersV4CreateOrderDtcEndpoint,
} from '@/api/generated';
import { DEFAULT_TELEHEALTH_CONTACT_TYPE } from '@/lib/constants';
import { type Order } from '@/types/order.type.ts';
import type { Patient } from '@/types/patient-info.type.ts';
import type {
  Contact,
  RelationshipFormData,
  ShippingAddressFormData,
} from '@/types/user-profile.type.ts';

type MinimalInsurance = {
  verificationKey: string;
};

type MutationParams = {
  productIds: string[];
  patient: Patient;
  address: ShippingAddressFormData;
  insurances: MinimalInsurance[];
  insuranceGated: boolean;
  providerId: string;
  contact: Contact;
  scheduledTime: string;
  /** From scheduling slot selection (session); defaults to video when absent */
  telehealthContactType?: string;
  /** Healthie appointment type id from the selected slot */
  telehealthAppointmentTypeId?: string;
};

type MutationOptions = Omit<
  UseMutationOptions<Order, Error, MutationParams>,
  'mutationFn' | 'retry'
>;

export function useCreateOrderMutation(options?: MutationOptions) {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return useMutation({
    mutationFn: async (params) => {
      const telehealth_contact_type =
        params.telehealthContactType ?? DEFAULT_TELEHEALTH_CONTACT_TYPE;
      const telehealth_appointment_type_id = params.telehealthAppointmentTypeId ?? '';

      const { data } = await ordersV4CreateOrderDtcEndpoint({
        body: {
          patient: {
            first_name: params.patient.firstName,
            last_name: params.patient.lastName,
            dob: new Date(params.patient.dateOfBirth).toISOString().split('T')[0]!,
            phone_number: params.contact.phoneNumber,
            gender: toApiGender(params.patient.gender),
            address: {
              street: params.address.addressLine1,
              complement: params.address.addressLine2 || null,
              city: params.address.city,
              state: params.address.state,
              zip_code: params.address.zip,
            },
          },
          insurances: params.insurances.map((insurance) => ({
            verification_key: insurance.verificationKey,
          })),
          primary_contact: {
            first_name: params.contact.firstName,
            last_name: params.contact.lastName,
            email: params.contact.email,
            phone_number: params.contact.phoneNumber,
            relationship: toApiRelationship(params.contact.relationship),
            phone_type: 'Mobile',
            preferred_language: 'English',
          },
          items: params.productIds,
          insurance_gated: params.insuranceGated,
          telehealth_scheduled_time: params.scheduledTime,
          telehealth_timezone: timeZone,
          telehealth_provider_id: params.providerId,
          telehealth_contact_type,
          telehealth_appointment_type_id,
        },
      });

      return transformToOrder(data);
    },
    ...options,
    retry: false,
  });
}

const transformToOrder = (data: CreateOrderResponse): Order => ({
  id: data.payload.id,
  appointment: data.payload.appointment
    ? {
        scheduledTime: new Date(data.payload.appointment.scheduled_time),
        timeZone: data.payload.appointment.timezone,
        provider: {
          name: data.payload.appointment.provider.name ?? null,
          phoneNumber: data.payload.appointment.provider.phone_number,
        },
      }
    : null,
});

function toApiGender(gender: Patient['gender']): CreateOrderInput['patient']['gender'] {
  switch (gender) {
    case 'male':
      return 'Male';
    case 'female':
      return 'Female';
    default:
      throw new Error(`Unsupported gender type: ${gender}`);
  }
}

function toApiRelationship(
  relationship: RelationshipFormData['relationship'] | undefined
): CreateOrderInput['primary_contact']['relationship'] {
  switch (relationship) {
    case undefined:
      return 'Self';
    case 'family-member':
      return 'Family Member';
    case 'non-family-caregiver':
      return 'Other';
    default:
      throw new Error(`Unsupported relationship type: ${relationship}`);
  }
}
